/**
 * useRatePrompt — polls eligibility, triggers the right surface for the platform.
 *
 * On native iOS/Android, this invokes the OS in-app review API
 * (`@capacitor-community/in-app-review`). Apple's SKStoreReviewController and
 * Google Play's In-App Review API both surface a native, friction-free
 * rating prompt without leaving the app — and Apple rate-limits to ~3 per
 * 365 days automatically.
 *
 * On the web, we surface a polite custom modal asking for a store rating
 * or quick feedback.
 *
 * Trigger contract: call `maybePromptAfter(eventName)` from positive moments
 * — completed booking, sent donation, attended livestream, repeat visit.
 * The hook handles eligibility, cooldown, and platform-specific surfacing.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { isNative, getPlatform } from "./mobile";

const SESSION_FLAG = "cauldron_rate_prompt_shown_this_session";

export function useRatePrompt() {
  const [showWebModal, setShowWebModal] = useState(false);

  const maybePromptAfter = useCallback(async (eventName = "") => {
    // One prompt per browser session at most (cheap client-side guard on top
    // of the 60-day server-side cooldown)
    if (sessionStorage.getItem(SESSION_FLAG)) return;
    try {
      const { data } = await api.get("/me/rate-prompt-eligible");
      if (!data?.eligible) return;
      sessionStorage.setItem(SESSION_FLAG, "1");
      if (isNative()) {
        try {
          const mod = await import("@capacitor-community/in-app-review");
          await mod.InAppReview.requestReview();
          await api.post("/me/rate-prompt-response", {
            response: "rated",
            platform: getPlatform(),
          });
        } catch (err) {
          // Native plugin not available (older build) or user dismissed
          await api.post("/me/rate-prompt-response", {
            response: "dismissed",
            platform: getPlatform(),
          });
        }
      } else {
        setShowWebModal(true);
      }
    } catch {
      /* network error — silently skip */
    }
  }, []);

  return { maybePromptAfter, showWebModal, setShowWebModal };
}

/**
 * Increments the user's profile-session counter. Call once per profile-page
 * mount; backend throttles to one increment per hour per user.
 */
export async function tickProfileSession() {
  try {
    await api.post("/me/rate-prompt-session-tick");
  } catch {
    /* non-critical */
  }
}
